"use client";

import { Show, SignInButton, UserButton } from "@clerk/nextjs";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { ReactNode } from "react";
import { Logo } from "@/components/Logo";
import { ModeToggle } from "@/components/ModeToggle";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { MobileNav } from "./MobileNav";
import {
  CONTACT_URL,
  type NavLink,
  PRODUCT_GROUPS,
  PRODUCT_TONE_CLASS,
  RESOURCE_LINKS,
} from "./nav-config";

function MenuLink({ link, active }: { link: NavLink; active: boolean }) {
  return (
    <NavigationMenuLink asChild active={active}>
      <Link
        href={link.href}
        className="block rounded-md p-3 no-underline outline-none transition-colors hover:bg-accent focus-visible:bg-accent"
      >
        <span className="block text-sm font-medium text-foreground">
          {link.label}
        </span>
        {link.description ? (
          <span className="mt-0.5 line-clamp-2 block text-xs leading-snug text-muted-foreground">
            {link.description}
          </span>
        ) : null}
      </Link>
    </NavigationMenuLink>
  );
}

function DesktopNav() {
  const pathname = usePathname();
  const isActive = (href: string) =>
    pathname === href || pathname?.startsWith(`${href}/`);

  return (
    <NavigationMenu>
      <NavigationMenuList className="gap-1">
        <NavigationMenuItem>
          <NavigationMenuTrigger className="bg-transparent text-foreground hover:bg-accent data-[state=open]:bg-accent">
            Products
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="grid w-[720px] grid-cols-3 gap-2 p-3">
              {PRODUCT_GROUPS.map((group) => (
                <div
                  key={group.name}
                  className={cn("rounded-md", PRODUCT_TONE_CLASS[group.tone])}
                >
                  <Link
                    href={group.href}
                    className="mb-1 block rounded-md border-l-2 border-product px-3 py-2 transition-colors hover:bg-accent"
                  >
                    <span className="block text-sm font-semibold text-foreground">
                      {group.name}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {group.tagline}
                    </span>
                  </Link>
                  <ul className="space-y-0.5">
                    {group.items.map((item) => (
                      <li key={item.href}>
                        <MenuLink link={item} active={isActive(item.href)} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        <NavigationMenuItem>
          <NavigationMenuTrigger className="bg-transparent text-foreground hover:bg-accent data-[state=open]:bg-accent">
            Resources
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="w-[320px] space-y-0.5 p-3">
              {RESOURCE_LINKS.map((item) => (
                <li key={item.href}>
                  <MenuLink link={item} active={isActive(item.href)} />
                </li>
              ))}
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}

interface SiteHeaderProps {
  /** Optional search box, rendered between the nav and the actions on large screens. */
  searchSlot?: ReactNode;
}

/**
 * The one public-site header. Two menus, at most two buttons, hamburger
 * below `lg`. Sign-in state comes from Clerk.
 */
export function SiteHeader({ searchSlot }: SiteHeaderProps) {
  return (
    <header className="glass-header sticky top-0 z-50">
      <nav
        aria-label="Main"
        className="section-container flex h-16 items-center justify-between gap-4"
      >
        <div className="flex min-w-0 items-center gap-6">
          <Logo />
          <div className="hidden lg:block">
            <DesktopNav />
          </div>
        </div>

        {searchSlot ? (
          <div className="hidden max-w-md flex-1 lg:block">{searchSlot}</div>
        ) : null}

        <div className="flex items-center gap-2">
          <Show when="signed-out">
            <SignInButton mode="modal">
              <Button
                variant="ghost"
                size="sm"
                className="hidden sm:inline-flex"
              >
                Sign In
              </Button>
            </SignInButton>
            <Button
              variant="premium"
              size="sm"
              className="hidden whitespace-nowrap sm:inline-flex"
              asChild
            >
              <a href={CONTACT_URL} target="_blank" rel="noreferrer">
                Request desk access
              </a>
            </Button>
          </Show>
          <Show when="signed-in">
            <Button
              variant="premium"
              size="sm"
              className="hidden whitespace-nowrap sm:inline-flex"
              asChild
            >
              <Link href="/console">Launch App</Link>
            </Button>
            <UserButton />
          </Show>
          <ModeToggle className="hidden sm:inline-flex" />
          <div className="lg:hidden">
            <MobileNav />
          </div>
        </div>
      </nav>
    </header>
  );
}
